import { ipaToFeatures, IpaType } from "../phonemes/ipa";
import { segmentPhonemes } from "../phonemes/segmentPhonemes";
import { NoteClass, NoteTone, PunctuationClass, WordUnit } from "./wordUnit";

/** turn unit into final output form */
export function unitToOutput(unit?: WordUnit, onlyPunc?: boolean): string {
  if (!unit) {
    return '';
  }
  switch (unit.punctuation) {
    case PunctuationClass.Pause: return '|';
    case PunctuationClass.MediumPause: return '|ː';
    case PunctuationClass.Stop:
      return unit.input === '?' ? '?' : unit.input === '!' ? '!' : '‖';
    case PunctuationClass.EndParagraph: return '‖';
    case PunctuationClass.StartQuote:
    case PunctuationClass.EndQuote: return '';
  }
  const ipa = unit.output ?? unit.input;
  return onlyPunc ? ipa : applyToIpa(ipa, unit);
}

/** use notes from 'unit' to possibly modify 'ipa' */
export function applyToIpa(ipa: string, unit: WordUnit): string {
  const notes = unit.notes;
  if (notes) {
    for (const note of notes) {
      ipa = applyOne(ipa, note, unit.tones)
    }
  }
  return ipa;
}

/** apply a single note to IPA */
function applyOne(ipa: string, note: NoteClass, tones?: NoteTone): string {
  switch (note) {
    case NoteClass.AddStress:
      return ipa.indexOf("ˈ") === -1 ? `ˈˈ${ipa}` : ipa.replace(/ˈ/g, 'ˈˈ');
    case NoteClass.LowerStress:
      return ipa.replace(/ˈ/g, 'ˌ');
    case NoteClass.LengthenVowels:
      return lengthenVowels(ipa);
    case NoteClass.Tone:
      return addTones(ipa, tones);
  }
  return ipa;
}

/** add ː after every vowel & diphthong to lengthen them */
function lengthenVowels(ipa: string): string {
  const oldSegments = segmentPhonemes(ipa).phonemes;
  let result = '';
  let i = 0;
  for (const segment of oldSegments) {
    if (segment.meta !== undefined) {
      result += segment.phoneme;
    } else {
      const features = ipaToFeatures(segment.phoneme);
      if (features?.includes('vowel') && !features?.includes('long')) {
        const next = i + 1 < oldSegments.length ? ipaToFeatures(oldSegments[i + 1].phoneme) : undefined;
        if (next !== undefined && next.includes('vowel') && next.includes('non-syllabic')) {
          // wait till after the 2nd half of the diphthong to add lengthener
          result += segment.phoneme;
        } else {
          result += `${segment.phoneme}ː`;
        }
      } else if (features?.includes('consonant') && features?.includes('syllabic') && !features?.includes('long')) {
        // lengthen syllabic consonants
        result += `${segment.phoneme}ː`;
      } else {
        result += segment.phoneme;
      }
    }
    i++;
  }
  return result;
}

/** remove stress & add appropriate tones */
function addTones(ipa: IpaType, tones?: NoteTone): IpaType {
  if (tones === undefined) {
    return ipa;
  }

  const noStress = ipa.replace(/ˈ|ˌ/g, '');
  const dots = noStress.match(/\./g);
  const syllCount = dots ? dots.length + 1 : 1;

  if (syllCount <= 1) {
    // 1 syllable, add tone to the end
    const tone = getTone(tones);
    return `${noStress}${tone}`;
  } else if (tones <= 5) {
    // single tone - add to every syllable
    const tone = getTone(tones);
    const tonesOnDot = noStress.replace(/\./g, `${tone}.`);
    return tonesOnDot + tone;
  } else if (tones === 15 || tones === 51) {
    // spread rising/falling tone across multiple syllables
    return spread2Tones(noStress, tones === 15, syllCount);
  } else {
    // spread 3 tones across multiple syllables
    return spread3Tones(noStress, tones === 242, syllCount);
  }
}

function getTone(tones: NoteTone): IpaType {
  switch (tones) {
    case 1: return '˩';
    case 2: return '˨';
    case 3: return '˧';
    case 4: return '˦';
    case 5: return '˥';
    case 15: return '˩˥';
    case 51: return '˥˩';
    case 242: return '˨˦˨';
    case 424: return '˦˨˦';
  }
  return '˧';
}

function spread2Tones(ipa: IpaType, rise: boolean, syllCount: number): IpaType {
  const first = rise ? '˩' : '˥';
  const last = rise ? '˥' : '˩';
  const midSyll = Math.floor(syllCount / 2);
  const hasMid = midSyll !== syllCount / 2;
  let tones: IpaType[] = [];
  for (let s = 0; s < syllCount; s++) {
    const t = (s < midSyll) ? first : (hasMid && s === midSyll) ? '˧' : last;
    tones.push(t);
  }
  return tonesToSyllables(ipa, tones);
}

function spread3Tones(ipa: IpaType, hill: boolean, syllCount: number): IpaType {
  if (syllCount === 2) {
    return tonesToSyllables(ipa, hill ? ['˨˦', '˨'] : ['˦˨', '˦']);
  }

  const end = hill ? '˨' : '˦';
  const mid = hill ? '˦' : '˨';
  let tones: IpaType[] = [end];
  for (let i = 1; i < syllCount - 1; i++) {
    tones.push(mid);
  }
  tones.push(end);
  return tonesToSyllables(ipa, tones);
}

// assign the tones one at a time to the syllables in 'ipa'
function tonesToSyllables(ipa: IpaType, tones: IpaType[]) {
  let newIpa = '';
  for (let i = 0, s = 0; i < ipa.length; i++) {
    if (ipa[i] === '.') {
      newIpa += tones[s++];
    }
    newIpa += ipa[i];
  }
  newIpa += tones[tones.length - 1];
  return newIpa;
}
